import { addDays, parseISO, subDays } from 'date-fns';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { WithdrawalStatus } from '@ndvr/portfolio-api';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate, titleCase, todayDate } from '@/utils';

type FilterType = 'status' | 'date-range';

const InputName = {
    withdrawalStates: 'withdrawalStates',
    startDate: 'startDate',
    endDate: 'endDate',
} as const;

const withdrawalStateOptions = Object.values(WithdrawalStatus).map((status) => ({
    value: status,
    label: titleCase(status),
}));

const TODAY = todayDate();
export const DEFAULT_START_DATE = subDays(TODAY, 7);
export const DEFAULT_END_DATE = addDays(TODAY, 30);
const MAX_DATE = formatDate(TODAY, 'yyyy-MM-dd');

const WithdrawalStatusFilter = () => {
    const { setValue } = useFormContext();
    const selectedStates: WithdrawalStatus[] = useWatch({ name: InputName.withdrawalStates }) ?? [];

    useEffect(() => {
        localStorage.setItem('withdrawalStates', JSON.stringify(selectedStates));
    }, [selectedStates]);

    return (
        <>
            {withdrawalStateOptions.map((option) => {
                const isSelected = selectedStates.includes(option.value);
                return (
                    <div
                        key={option.value}
                        className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                        onClick={() =>
                            setValue(
                                InputName.withdrawalStates,
                                isSelected
                                    ? selectedStates.filter((s) => s !== option.value)
                                    : [...selectedStates, option.value],
                            )
                        }
                    >
                        <Checkbox checked={isSelected} />
                        <span>{option.label}</span>
                    </div>
                );
            })}
        </>
    );
};

const DateRangeFilter = () => {
    const { control } = useFormContext();
    return (
        <div className="flex flex-col gap-4 p-2">
            <div className="flex flex-col gap-1.5">
                <Label>From date</Label>
                <Controller
                    control={control}
                    name={InputName.startDate}
                    render={({ field }) => (
                        <Input
                            type="date"
                            max={MAX_DATE}
                            value={formatDate(field.value, 'yyyy-MM-dd')}
                            onChange={(e) => field.onChange(e.target.value ? parseISO(e.target.value) : undefined)}
                        />
                    )}
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <Label>To date</Label>
                <Controller
                    control={control}
                    name={InputName.endDate}
                    render={({ field }) => (
                        <Input
                            type="date"
                            max={MAX_DATE}
                            value={formatDate(field.value, 'yyyy-MM-dd')}
                            onChange={(e) => field.onChange(e.target.value ? parseISO(e.target.value) : undefined)}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export const WithdrawalMultiFilter = () => {
    const {
        resetField,
        formState: { dirtyFields },
    } = useFormContext();
    const withdrawalStates: WithdrawalStatus[] = useWatch({ name: InputName.withdrawalStates }) ?? [];

    const sections: MultiFilterSection<FilterType>[] = [
        {
            id: 'status',
            label: 'Status',
            count: withdrawalStates.length,
            onClear: () => {
                resetField(InputName.withdrawalStates);
                localStorage.removeItem('withdrawalStates');
            },
            content: <WithdrawalStatusFilter />,
        },
        {
            id: 'date-range',
            label: 'Date Range',
            count: [dirtyFields.startDate, dirtyFields.endDate].filter(Boolean).length,
            onClear: () => {
                resetField(InputName.startDate);
                resetField(InputName.endDate);
            },
            content: <DateRangeFilter />,
        },
    ];

    return (
        <div className="relative">
            <MultiFilter
                sections={sections}
                defaultSectionId="status"
                dropdownClassName="h-80 w-96 p-0"
                contentClassName="min-h-0 flex-1 overflow-y-auto p-2"
            />
        </div>
    );
};
